import { useDateFormat, useTimeAgo } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

import isNil from 'lodash/isNil'

/**
 * i18n formats (number/date) use Intl API behind the hood and if you pass a wrong date
 * it will broke your app
 * Instead of try catch everywhere, we use this function to format dates/numbers
 */
export function useI18nSafeFormat() {
  const { n, t, locale } = useI18n()

  function formatDateRange(dates: string[] | Date[] | undefined[] | null[], format = 'DD MMMM YYYY') {
    return dates
      .map((date, idx) => {
        if (idx === 0)
          return t('generics.date-ranges.from', { date: formatDate(date, format) })

        return t('generics.date-ranges.to', { date: formatDate(date, format) })
      })
      .join(' ')
  }
  /**
   * Flexible date formatter
   * @param date
   * @param format
   *
   * @example formatDate(date)
   * @example formatDate(date, 'dd/MM/YYYY')
   */
  function formatDate(date: string | Date | undefined | null, format = 'DD MMMM YYYY') {
    if (!date)
      return ''

    try {
      return useDateFormat(date, format, { locale: locale.value }).value
    }
    catch (error) {
      console.warn('Error while trying to format date', error)
    }
    return ''
  }

  function getTimeAgo(date: string | Date) {
    return useTimeAgo(typeof date === 'string' ? new Date(date) : date).value
  }

  /**
   * Standard currency formatter to be used across app
   * @param amount
   * @param currencyCode
   * @param compact
   */
  function fmtCurrency(amount: number | string | undefined | null, currencyCode: string, compact?: boolean) {
    const options: Intl.NumberFormatOptions = {
      currency: currencyCode,
      currencyDisplay: 'code',
      style: 'currency',
      minimumFractionDigits: 2,
    }

    return fmtNumber(amount, compact, options)
  }

  /**
   * Edge cases formatter when we need to display only the currency symbol
   * @param amount
   * @param currencyCode
   * @param compact
   */
  function fmtCurrencySymbol(amount: number | string | undefined | null, currencyCode: string, compact?: boolean) {
    const options: Intl.NumberFormatOptions = {
      currency: currencyCode,
      currencyDisplay: 'symbol',
      style: 'currency',
      minimumFractionDigits: 0,
    }

    return fmtNumber(amount, compact, options)
  }

  /**
   * Already divided is when we had already an operation that give you a result within => 0 to <= 1
   * @param amount
   * @param alreadyDivided
   */
  function fmtPercentage(amount: number | string, alreadyDivided?: boolean) {
    try {
      let _amount = typeof amount === 'string' ? Number.parseFloat(amount) : amount

      if (!alreadyDivided)
        _amount = _amount / 100

      return fmtNumber(_amount, false, {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 3,
        maximumSignificantDigits: undefined,
      })
    }
    catch (_err) {
      return '-'
    }
  }

  /**
   * Core formatter used by siblings
   * Should not be edited directly as it will impact all siblings
   * @param amount
   * @param compact
   * @param options
   */
  function fmtNumber(
    amount: number | string | undefined | null,
    compact?: boolean,
    options: Intl.NumberFormatOptions = {},
  ) {
    const _options = {
      ...options,
      maximumFractionDigits: 3,
    }

    if (compact) {
      _options.notation = 'compact'
      _options.compactDisplay = 'short'
    }
    try {
      const _amount = typeof amount === 'string' ? Number.parseFloat(amount) : amount
      if (Number.isNaN(_amount) || isNil(amount))
        return '-'
      return n(_amount || 0, _options)
    }
    catch (_err) {
      return '-'
    }
  }

  return { getTimeAgo, formatDate, formatDateRange, fmtPercentage, fmtCurrency, fmtCurrencySymbol, fmtNumber }
}
